import type {
  BlankInventory,
  CanonicalPolicy,
  DocumentCapability,
  IssueResponse,
  PaymentResponse,
  ProviderCapabilities,
  QuoteResponse,
  Schedule,
} from "../../types/lifecycle.ts";

export class LifecycleResponseError extends Error {
  constructor(readonly path: string) {
    super(`Invalid lifecycle response: ${path}`);
    this.name = "LifecycleResponseError";
  }
}

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const record = (value: unknown, path: string) => {
  if (!isRecord(value)) throw new LifecycleResponseError(path);
  return value;
};
const check: (valid: boolean, path: string) => asserts valid = (
  valid,
  path
) => {
  if (!valid) throw new LifecycleResponseError(path);
};
const text = (value: unknown) => typeof value === "string";
const nonempty = (value: unknown) =>
  text(value) && String(value).trim().length > 0;
const finite = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value);
const list = (value: unknown, validate: (row: unknown) => void) => {
  check(Array.isArray(value), "array");
  value.forEach(validate);
};
const providerIds = [
  "ozk",
  "dzi",
  "generali",
  "levins",
  "bulins",
  "dallbogg",
  "armeec",
  "euroins",
];
export const isProvider = (value: unknown) =>
  providerIds.includes(String(value));
const money = (value: unknown) => {
  const row = record(value, "money");
  check(
    [row.amount, row.eur, row.bgn].every(finite) &&
      ["EUR", "BGN"].includes(String(row.currency)),
    "money"
  );
};
const policy = (value: unknown) => {
  const row = record(value, "policy");
  check(
    isProvider(row.provider) && nonempty(row.policyNumber),
    "policy identifier"
  );
  for (const key of [
    "greenCardNumber",
    "status",
    "providerStatusCode",
    "issuedAt",
    "source",
  ])
    if (row[key] != null) check(text(row[key]), `policy.${key}`);
  if (row.premium != null) money(row.premium);
};
const idempotent = (value: unknown) => {
  const row = record(value, "idempotent");
  check(
    nonempty(row.messageId) && typeof row.replayed === "boolean",
    "idempotent"
  );
};
export const validateAdvisories = (value: unknown) =>
  list(value, (entry) => {
    const row = record(entry, "advisory");
    check([row.code, row.message, row.severity].every(text), "advisory");
  });
const booking = (value: unknown) => {
  const row = record(value, "booking");
  check(
    ["EUR", "BGN"].includes(String(row.currency)) &&
      finite(row.installmentCount),
    "booking"
  );
  for (const key of ["coverageStart", "coverageEnd"])
    if (row[key] != null) check(text(row[key]), `booking.${key}`);
  const totals = [
    "basePremium",
    "guaranteeFund",
    "tax",
    "stickerFee",
    "otherFees",
    "total",
  ];
  totals.forEach((key) => money(row[key]));
  money(row.insuredAmount);
  list(row.installments, (entry) => {
    const installment = record(entry, "booking installment");
    check(finite(installment.sequence), "booking sequence");
    totals.forEach((key) => money(installment[key]));
  });
};
const documents = (value: unknown) =>
  list(value, (entry) => {
    const row = record(entry, "document");
    check(
      [
        "policy",
        "green-card",
        "payment-receipt",
        "protocol",
        "offer",
        "terms",
        "report",
      ].includes(String(row.type)) && typeof row.available === "boolean",
      "document"
    );
    if (row.reason != null) check(text(row.reason), "document reason");
    if (row.oneShot != null)
      check(typeof row.oneShot === "boolean", "document retention");
  });

const vehicleType = (value: unknown) =>
  check(
    [
      "passenger",
      "freight",
      "bus",
      "motorcycle",
      "moped",
      "trailer",
      "tractor",
    ].includes(String(value)),
    "vehicle type"
  );
const owner = (value: unknown) => {
  const row = record(value, "owner");
  for (const key of ["id", "name", "type"])
    if (row[key] != null) check(text(row[key]), `owner.${key}`);
  if (row.addresses != null)
    list(row.addresses, (value) => {
      const address = record(value, "address");
      for (const key of ["ekatte", "line"])
        if (address[key] != null) check(text(address[key]), `address.${key}`);
    });
};

// Casts are confined to this boundary, after checking the fields consumed by the UI.
export const parseIssue = (value: unknown): IssueResponse => {
  const row = record(value, "issue");
  policy(row.policy);
  booking(row.booking);
  validateAdvisories(row.advisories);
  idempotent(row.idempotent);
  const local = record(row.localWrite, "localWrite");
  check(
    [
      local.policyId,
      local.clientId,
      local.vehicleId,
      local.divisionId,
      local.agentId,
    ].every(
      (value) => finite(value) && Number.isInteger(value) && Number(value) > 0
    ) &&
      nonempty(local.policyNumber) &&
      typeof local.replayed === "boolean",
    "localWrite"
  );
  check(
    local.policyNumber === record(row.policy, "policy").policyNumber,
    "local policy number"
  );
  return row as unknown as IssueResponse;
};
export const parsePayment = (value: unknown): PaymentResponse => {
  const row = record(value, "payment");
  policy(row.policy);
  validateAdvisories(row.advisories);
  idempotent(row.idempotent);
  const receipt = record(row.receipt, "receipt");
  money(receipt.amount);
  check(
    text(receipt.reportedAt) &&
      text(receipt.paymentMethod) &&
      (receipt.receiptNumber === null || text(receipt.receiptNumber)),
    "receipt"
  );
  return row as unknown as PaymentResponse;
};
export const parseQuote = (value: unknown): QuoteResponse => {
  const row = record(value, "quote response");
  check(nonempty(row.quoteHandle) && nonempty(row.expiresAt), "quote handle");
  check(
    nonempty(record(row.quote, "priced quote").registrationPlate),
    "priced quote"
  );
  list(row.offers, (entry) => {
    const offer = record(entry, "offer");
    check(
      isProvider(offer.provider) &&
        typeof offer.available === "boolean" &&
        typeof offer.canIssue === "boolean",
      "offer"
    );
    for (const key of ["errorCode", "reason", "issueErrorCode", "issueReason"])
      if (offer[key] != null) check(text(offer[key]), `offer.${key}`);
    if (offer.available) money(offer.premium);
    if (offer.canIssue) check(offer.available === true, "issuable offer");
    if (offer.booking != null) booking(offer.booking);
  });
  if (row.vehicle != null) {
    const vehicle = record(row.vehicle, "vehicle");
    for (const key of ["make", "model", "chassis"])
      if (vehicle[key] != null) check(text(vehicle[key]), `vehicle.${key}`);
    for (const key of [
      "seats",
      "displacement",
      "productionYear",
      "maxWeightF1",
      "enginePower",
    ])
      if (vehicle[key] != null) check(finite(vehicle[key]), `vehicle.${key}`);
    if (vehicle.vehicleType != null) vehicleType(vehicle.vehicleType);
  }
  if (row.holder != null) {
    const holder = record(row.holder, "holder");
    for (const key of [
      "firstName",
      "surname",
      "familyName",
      "companyName",
      "name",
      "pin",
      "settlementCode",
      "street",
    ])
      if (holder[key] != null) check(text(holder[key]), `holder.${key}`);
    if (holder.customerType != null)
      check(
        ["natural", "legal"].includes(String(holder.customerType)),
        "customer type"
      );
    if (holder.roles != null) {
      const roles = record(holder.roles, "roles");
      for (const key of ["user", "acquirer"])
        if (roles[key] != null) owner(roles[key]);
    }
  }
  if (row.resolved != null) {
    const resolved = record(row.resolved, "resolved");
    if (resolved.purpose != null)
      check(text(resolved.purpose), "resolved purpose");
    if (resolved.vehicleType != null) vehicleType(resolved.vehicleType);
    if (resolved.leasing != null) {
      const leasing = record(resolved.leasing, "leasing");
      check(
        typeof leasing.detected === "boolean" &&
          typeof leasing.lesseeRequired === "boolean",
        "leasing"
      );
      for (const key of ["ownerPin", "ownerName", "lesseePin"])
        if (leasing[key] != null) check(text(leasing[key]), `leasing.${key}`);
    }
  }
  if (row.owners != null) list(row.owners, owner);
  // Keep the server's priced object, including fields unknown to this client.
  return row as unknown as QuoteResponse;
};
export const parseSchedule = (
  value: unknown
): { policy: CanonicalPolicy; schedule: Schedule } => {
  const row = record(value, "schedule response");
  policy(row.policy);
  const schedule = record(row.schedule, "schedule");
  check(
    isProvider(schedule.provider) &&
      ["full", "unpaid-only", "projection"].includes(
        String(schedule.completeness)
      ),
    "schedule"
  );
  check(
    schedule.provider === record(row.policy, "policy").provider,
    "schedule provider"
  );
  if (schedule.note != null) check(text(schedule.note), "schedule note");
  list(schedule.installments, (entry) => {
    const installment = record(entry, "installment");
    check(
      finite(installment.sequence) &&
        ["paid", "due", "unknown"].includes(String(installment.status)) &&
        (installment.dueDate === null || text(installment.dueDate)),
      "installment"
    );
    money(installment.amount);
    check(text(installment.paymentTarget), "payment target");
  });
  return row as unknown as { policy: CanonicalPolicy; schedule: Schedule };
};
export const parseBlanks = (value: unknown): BlankInventory => {
  const row = record(value, "blank inventory");
  check(isProvider(row.provider), "blank provider");
  for (const kind of ["sticker", "green-card"])
    list(row[kind], (entry) => {
      const series = record(entry, "blank series");
      check(text(series.series) && text(series.label), "blank series");
      list(series.available, (number) =>
        check(nonempty(number), "blank number")
      );
      check(
        series.nextAvailable === null || text(series.nextAvailable),
        "next blank"
      );
    });
  return row as unknown as BlankInventory;
};
export const parseCapabilities = (
  value: unknown
): { providers: ProviderCapabilities[] } => {
  const row = record(value, "capabilities");
  list(row.providers, (entry) => {
    const provider = record(entry, "provider");
    check(isProvider(provider.provider), "provider");
    Object.values(record(provider.operations, "operations")).forEach(
      (entry) => {
        const capability = record(entry, "capability");
        check(
          [
            "supported",
            "not_configured",
            "not_implemented",
            "not_supported",
          ].includes(String(capability.state)),
          "capability state"
        );
      }
    );
    if (provider.displayName != null)
      check(text(provider.displayName), "provider display name");
    documents(provider.documents);
    if (provider.installments != null) {
      const installments = record(
        provider.installments,
        "installment capabilities"
      );
      if (installments.allowedCounts != null)
        list(installments.allowedCounts, (n) =>
          check(finite(n) && Number(n) > 0, "installment count")
        );
    }
  });
  return row as unknown as { providers: ProviderCapabilities[] };
};
export const parseDocuments = (
  value: unknown
): { policy: CanonicalPolicy; documents: DocumentCapability[] } => {
  const row = record(value, "documents");
  policy(row.policy);
  documents(row.documents);
  return row as unknown as {
    policy: CanonicalPolicy;
    documents: DocumentCapability[];
  };
};
export const parseCommission = (
  value: unknown
): { commissionPercentage: number } => {
  const row = record(value, "commission");
  // Validate the response shape here; editable commission limits belong to the form.
  check(
    finite(row.commissionPercentage) && Number(row.commissionPercentage) >= 0,
    "commission percentage"
  );
  return { commissionPercentage: Number(row.commissionPercentage) };
};
